// Fichier: src/pages/accounting/ChartOfAccounts.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip, Tooltip,
  Snackbar, Alert, InputAdornment, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon, Search as SearchIcon, PlaylistAdd as PlaylistAddIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import {
  collection, onSnapshot, query, orderBy, doc, setDoc,
  deleteDoc, writeBatch
} from 'firebase/firestore';

// ── Classes du PCMN (adapté ASBL, AR 29/04/2019) ────────────────────────
const CLASSES = [
  { value: 1, label: 'Classe 1 — Fonds social, provisions, dettes > 1 an', side: 'credit' },
  { value: 2, label: 'Classe 2 — Actifs immobilisés', side: 'debit' },
  { value: 3, label: 'Classe 3 — Stocks', side: 'debit' },
  { value: 4, label: 'Classe 4 — Créances et dettes ≤ 1 an', side: 'debit' },
  { value: 5, label: 'Classe 5 — Trésorerie', side: 'debit' },
  { value: 6, label: 'Classe 6 — Charges', side: 'debit' },
  { value: 7, label: 'Classe 7 — Produits', side: 'credit' }
];

// ── Plan comptable de base, adapté à MKD Studio (pas de TVA, personnel salarié) ──
const DEFAULT_ACCOUNTS = [
  // Classe 1
  { code: '100', label: 'Patrimoine de départ', classNumber: 1, normalSide: 'credit' },
  { code: '130', label: 'Fonds affectés', classNumber: 1, normalSide: 'credit' },
  { code: '140', label: 'Résultat reporté', classNumber: 1, normalSide: 'credit' },
  { code: '160', label: 'Provisions pour risques et charges', classNumber: 1, normalSide: 'credit' },
  { code: '170', label: "Dettes à plus d'un an (emprunts)", classNumber: 1, normalSide: 'credit' },

  // Classe 2
  { code: '210', label: 'Immobilisations incorporelles', classNumber: 2, normalSide: 'debit' },
  { code: '220', label: 'Terrains et constructions', classNumber: 2, normalSide: 'debit' },
  { code: '230', label: 'Installations, machines et outillage', classNumber: 2, normalSide: 'debit' },
  { code: '240', label: 'Mobilier et matériel roulant', classNumber: 2, normalSide: 'debit' },

  // Classe 3
  { code: '340', label: 'Stock marchandises (boutique)', classNumber: 3, normalSide: 'debit' },

  // Classe 4
  { code: '400', label: 'Clients', classNumber: 4, normalSide: 'debit' },
  { code: '404', label: 'Créances diverses', classNumber: 4, normalSide: 'debit' },
  { code: '440', label: 'Fournisseurs', classNumber: 4, normalSide: 'credit' },
  { code: '450', label: 'Précompte professionnel retenu', classNumber: 4, normalSide: 'credit' },
  { code: '453', label: 'ONSS', classNumber: 4, normalSide: 'credit' },
  { code: '454', label: 'Rémunérations dues', classNumber: 4, normalSide: 'credit' },
  { code: '4541', label: 'Pécule de vacances à payer', classNumber: 4, normalSide: 'credit' },
  { code: '489', label: 'Autres dettes diverses', classNumber: 4, normalSide: 'credit' },
  { code: '490', label: 'Charges à reporter', classNumber: 4, normalSide: 'debit' },
  { code: '492', label: 'Produits à reporter', classNumber: 4, normalSide: 'credit' },

  // Classe 5
  { code: '550', label: 'Banque', classNumber: 5, normalSide: 'debit' },
  { code: '570', label: 'Caisse', classNumber: 5, normalSide: 'debit' },

  // Classe 6 — Charges
  { code: '600', label: 'Achats de fournitures', classNumber: 6, normalSide: 'debit' },
  { code: '601', label: 'Achats marchandises (boutique)', classNumber: 6, normalSide: 'debit' },
  { code: '602', label: 'Matériel pédagogique et costumes', classNumber: 6, normalSide: 'debit' },
  { code: '610', label: 'Locations et charges locatives', classNumber: 6, normalSide: 'debit' },
  { code: '611', label: 'Entretien et réparations', classNumber: 6, normalSide: 'debit' },
  { code: '612', label: 'Assurances', classNumber: 6, normalSide: 'debit' },
  { code: '613', label: "Rémunérations d'indépendants (profs freelance)", classNumber: 6, normalSide: 'debit' },
  { code: '614', label: 'Publicité et marketing', classNumber: 6, normalSide: 'debit' },
  { code: '615', label: 'Honoraires (comptable, avocat...)', classNumber: 6, normalSide: 'debit' },
  { code: '616', label: 'Frais de déplacement', classNumber: 6, normalSide: 'debit' },
  { code: '617', label: 'Frais postaux et télécommunications', classNumber: 6, normalSide: 'debit' },
  { code: '618', label: 'Frais bancaires', classNumber: 6, normalSide: 'debit' },
  { code: '619', label: 'Autres services et biens divers', classNumber: 6, normalSide: 'debit' },
  { code: '620', label: 'Rémunérations du personnel', classNumber: 6, normalSide: 'debit' },
  { code: '621', label: 'Cotisations patronales ONSS', classNumber: 6, normalSide: 'debit' },
  { code: '622', label: 'Assurances extra-légales du personnel', classNumber: 6, normalSide: 'debit' },
  { code: '623', label: 'Autres frais de personnel', classNumber: 6, normalSide: 'debit' },
  { code: '624', label: 'Pécules de vacances', classNumber: 6, normalSide: 'debit' },
  { code: '625', label: 'Assurance accidents du travail', classNumber: 6, normalSide: 'debit' },
  { code: '630', label: 'Dotations aux amortissements', classNumber: 6, normalSide: 'debit' },
  { code: '640', label: "Charges diverses d'exploitation", classNumber: 6, normalSide: 'debit' },
  { code: '650', label: 'Frais financiers', classNumber: 6, normalSide: 'debit' },
  { code: '660', label: 'Charges exceptionnelles', classNumber: 6, normalSide: 'debit' },

  // Classe 7 — Produits
  { code: '700', label: 'Cotisations des membres', classNumber: 7, normalSide: 'credit' },
  { code: '701', label: 'Dons reçus', classNumber: 7, normalSide: 'credit' },
  { code: '703', label: 'Subsides', classNumber: 7, normalSide: 'credit' },
  { code: '730', label: 'Inscriptions aux cours', classNumber: 7, normalSide: 'credit' },
  { code: '731', label: 'Ventes boutique', classNumber: 7, normalSide: 'credit' },
  { code: '732', label: 'Prestations artistiques (spectacles, animations)', classNumber: 7, normalSide: 'credit' },
  { code: '740', label: "Produits divers d'exploitation", classNumber: 7, normalSide: 'credit' },
  { code: '750', label: 'Produits financiers', classNumber: 7, normalSide: 'credit' },
  { code: '760', label: 'Produits exceptionnels', classNumber: 7, normalSide: 'credit' }
];

const emptyForm = { code: '', label: '', classNumber: 6, normalSide: 'debit', active: true };

function classInfo(n) {
  return CLASSES.find((c) => c.value === Number(n));
}

const ChartOfAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'accounting_accounts'), orderBy('code'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      const matchClass = classFilter === 'all' || Number(a.classNumber) === Number(classFilter);
      const s = search.trim().toLowerCase();
      const matchSearch = !s || a.code?.toLowerCase().includes(s) || a.label?.toLowerCase().includes(s);
      return matchClass && matchSearch;
    });
  }, [accounts, search, classFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (account) => {
    setEditingId(account.id);
    setForm({
      code: account.code || '',
      label: account.label || '',
      classNumber: account.classNumber || 6,
      normalSide: account.normalSide || 'debit',
      active: account.active !== false
    });
    setDialogOpen(true);
  };

  const handleClassChange = (e) => {
    const classNumber = Number(e.target.value);
    const info = classInfo(classNumber);
    setForm((f) => ({ ...f, classNumber, normalSide: info?.side || f.normalSide }));
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.label.trim()) {
      setSnackbar({ open: true, message: 'Le code et le libellé sont obligatoires.', severity: 'error' });
      return;
    }
    const duplicate = accounts.find((a) => a.code === form.code.trim() && a.id !== editingId);
    if (duplicate) {
      setSnackbar({ open: true, message: 'Ce code de compte existe déjà.', severity: 'error' });
      return;
    }
    try {
      const payload = {
        code: form.code.trim(),
        label: form.label.trim(),
        classNumber: Number(form.classNumber),
        normalSide: form.normalSide,
        active: form.active
      };
      const ref = editingId
        ? doc(db, 'accounting_accounts', editingId)
        : doc(collection(db, 'accounting_accounts'));
      await setDoc(ref, { ...payload, updatedAt: new Date(), ...(editingId ? {} : { createdAt: new Date() }) }, { merge: true });
      setDialogOpen(false);
      setSnackbar({ open: true, message: 'Compte enregistré.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erreur lors de l'enregistrement.", severity: 'error' });
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Supprimer le compte ${account.code} — ${account.label} ?`)) return;
    try {
      await deleteDoc(doc(db, 'accounting_accounts', account.id));
      setSnackbar({ open: true, message: 'Compte supprimé.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression.', severity: 'error' });
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const existingCodes = new Set(accounts.map((a) => a.code));
      const toAdd = DEFAULT_ACCOUNTS.filter((a) => !existingCodes.has(a.code));
      if (toAdd.length === 0) {
        setSnackbar({ open: true, message: 'Le plan comptable de base est déjà en place.', severity: 'info' });
        return;
      }
      const batch = writeBatch(db);
      toAdd.forEach((acc) => {
        const ref = doc(collection(db, 'accounting_accounts'));
        batch.set(ref, { ...acc, active: true, createdAt: new Date() });
      });
      await batch.commit();
      setSnackbar({ open: true, message: `${toAdd.length} compte(s) ajouté(s) depuis le plan de base.`, severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Erreur lors de l'initialisation.", severity: 'error' });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Plan comptable</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<PlaylistAddIcon />} onClick={handleSeed} disabled={seeding}>
            Initialiser / compléter avec le plan de base
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Nouveau compte
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Rechercher un code ou un libellé..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <ToggleButtonGroup value={classFilter} exclusive size="small" onChange={(e, val) => val !== null && setClassFilter(val)}>
          <ToggleButton value="all">Toutes</ToggleButton>
          {CLASSES.map((c) => (
            <ToggleButton key={c.value} value={c.value}>{`Cl. ${c.value}`}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Libellé</strong></TableCell>
              <TableCell><strong>Classe</strong></TableCell>
              <TableCell><strong>Sens normal</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun compte. Utilise « Initialiser avec le plan de base » pour démarrer.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{a.code}</TableCell>
                <TableCell>{a.label}</TableCell>
                <TableCell>{classInfo(a.classNumber)?.label.split(' — ')[0] || a.classNumber}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={a.normalSide === 'credit' ? 'Crédit' : 'Débit'}
                    color={a.normalSide === 'credit' ? 'secondary' : 'primary'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={a.active !== false ? 'Actif' : 'Inactif'} color={a.active !== false ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" onClick={() => handleDelete(a)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Modifier le compte' : 'Nouveau compte'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Code du compte"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="ex: 613"
            fullWidth
          />
          <TextField
            label="Libellé"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            fullWidth
          />
          <TextField select label="Classe" value={form.classNumber} onChange={handleClassChange} fullWidth>
            {CLASSES.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Sens normal du compte"
            value={form.normalSide}
            onChange={(e) => setForm((f) => ({ ...f, normalSide: e.target.value }))}
            helperText="Détermine si une augmentation du compte s'enregistre au débit ou au crédit"
            fullWidth
          >
            <MenuItem value="debit">Débit</MenuItem>
            <MenuItem value="credit">Crédit</MenuItem>
          </TextField>
          <TextField
            select
            label="Statut"
            value={form.active ? 'active' : 'inactive'}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'active' }))}
            fullWidth
          >
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChartOfAccounts;